import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Play, Users, Trophy, Clock, Star, Shield, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useRef } from "react";

const stats = [
  { icon: Users, value: 50, suffix: "+", label: "Clients" },
  { icon: Trophy, value: 100, suffix: "%", label: "Satisfaits" },
  { icon: Clock, value: 48, suffix: "h", label: "Délai moyen" },
  { icon: Star, value: 4.9, suffix: "/5", label: "Note", decimals: 1 },
];

const trustLogos = [
  "Design Premium", "Parfait sur mobile", "Visible sur Google", "Dark & Light", "Ultra Rapide", "100% Personnalisé"
];

function AnimatedCounter({ value, suffix, decimals = 0 }: { value: number; suffix: string; decimals?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        const duration = 1500;
        const start = Date.now();
        const animate = () => {
          const elapsed = Date.now() - start;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Number((eased * value).toFixed(decimals)));
          if (progress < 1) requestAnimationFrame(animate);
        };
        animate();
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, decimals]);

  return <span ref={ref}>{count}{suffix}</span>;
}

const SellHero = () => {
  return (
    <section className="relative overflow-hidden min-h-[90vh] flex items-center">
      <div className="container mx-auto px-4 sm:px-6 relative py-12 sm:py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left - Copy */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* Urgency badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-5"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-bold border border-primary/20 backdrop-blur-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                🔥 3 places restantes ce mois-ci
              </span>
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-black leading-[1.05] mb-5 tracking-tight">
              Démarquez-vous
              <br />
              <span className="text-foreground/90">avec un portfolio</span>
              <br />
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent bg-[length:200%] animate-[gradient_3s_ease_infinite]">
                  professionnel
                </span>
                <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 300 12" fill="none">
                  <motion.path 
                    d="M2 10C50 2 100 2 150 6C200 10 250 4 298 8" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth="3" 
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 1, duration: 0.8 }}
                  />
                </svg>
              </span>
              {" "}
              <span className="text-foreground/90">qui </span>
              <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">claque</span>
            </h1>

            <motion.p 
              className="text-sm sm:text-base md:text-lg text-muted-foreground mb-6 leading-relaxed max-w-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Quel que soit votre métier, je crée votre vitrine en ligne <strong className="text-foreground font-semibold">haut de gamme</strong>, 
              personnalisée à votre image. Design premium, animations fluides, visible sur Google — 
              <strong className="text-foreground font-semibold"> livré en 48h</strong>.
            </motion.p>

            {/* Micro trust signals */}
            <motion.div
              className="flex flex-wrap gap-3 sm:gap-4 mb-6 text-xs sm:text-sm text-muted-foreground"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              {[
                { icon: CheckCircle, text: "Satisfait ou remboursé" },
                { icon: Shield, text: "Paiement sécurisé" },
                { icon: Clock, text: "Livré en 48h" },
              ].map(({ icon: Icon, text }) => (
                <span key={text} className="flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                  <span>{text}</span>
                </span>
              ))}
            </motion.div>

            <motion.div 
              className="flex flex-col sm:flex-row gap-3 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <a href="#pricing">
                <Button size="lg" className="text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 hover:-translate-y-0.5 group w-full sm:w-auto font-bold">
                  🚀 Créer ma vitrine
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a href="/cv" target="_blank" rel="noopener">
                <Button size="lg" variant="outline" className="text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6 border-primary/40 hover:border-primary hover:bg-primary/10 backdrop-blur-sm group relative overflow-hidden w-full sm:w-auto font-semibold">
                  <span className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <Play className="w-4 h-4 mr-2 text-primary" />
                  <span className="relative">Voir un exemple</span>
                </Button>
              </a>
            </motion.div>

            {/* Animated stats */}
            <motion.div 
              className="grid grid-cols-4 gap-2 sm:gap-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              {stats.map((stat, i) => (
                <motion.div 
                  key={stat.label}
                  className="text-center p-2 sm:p-3 rounded-xl bg-card/50 border border-border/30 backdrop-blur-sm"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 + i * 0.1 }}
                >
                  <stat.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary mx-auto mb-1" />
                  <p className="text-lg sm:text-xl font-extrabold text-foreground">
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} decimals={stat.decimals} />
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right - Live Browser Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 40, rotateY: -5 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
            className="relative overflow-hidden hidden sm:block"
          >
            {/* Glow behind */}
            <div className="absolute -inset-8 bg-gradient-to-r from-primary/20 via-accent/15 to-primary/20 rounded-3xl blur-2xl opacity-60" />
            
            {/* Browser mockup */}
            <div className="relative rounded-2xl border border-border/50 overflow-hidden shadow-2xl bg-card protected-content">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-secondary/60 border-b border-border/40">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-destructive/60" />
                  <div className="w-3 h-3 rounded-full bg-enterprise-warning/60" />
                  <div className="w-3 h-3 rounded-full bg-enterprise-success/60" />
                </div>
                <div className="flex-1 mx-3">
                  <div className="h-6 rounded-lg bg-background/80 flex items-center px-3">
                    <span className="text-xs text-primary font-mono">🔒 votre-nom.com</span>
                  </div>
                </div>
              </div>
              
              {/* Portfolio preview content */}
              <div className="p-5 space-y-4 bg-gradient-to-br from-background to-secondary/20 min-h-[380px]">
                {/* Nav */}
                <div className="flex items-center justify-between">
                  <div className="h-4 w-28 bg-gradient-to-r from-primary to-accent rounded" />
                  <div className="flex gap-4">
                    {[1, 2, 3, 4].map((n) => (
                      <motion.div 
                        key={n} 
                        className="h-2 w-12 bg-primary/15 rounded"
                        animate={{ opacity: [0.3, 0.6, 0.3] }}
                        transition={{ repeat: Infinity, duration: 2, delay: n * 0.3 }}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Hero section mockup */}
                <div className="grid grid-cols-2 gap-4 py-3">
                  <div className="space-y-3">
                    <motion.div 
                      className="h-5 w-3/4 bg-gradient-to-r from-primary to-accent rounded-lg"
                      animate={{ opacity: [0.7, 1, 0.7] }}
                      transition={{ repeat: Infinity, duration: 3 }}
                    />
                    <div className="h-3 w-full bg-primary/15 rounded" />
                    <div className="h-3 w-5/6 bg-primary/10 rounded" />
                    <div className="flex gap-2 pt-2">
                      <div className="h-7 w-20 bg-gradient-to-r from-primary to-accent rounded-lg" />
                      <div className="h-7 w-16 border border-primary/30 rounded-lg" />
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <motion.div 
                      className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/20"
                      animate={{ rotate: [0, 3, -3, 0] }}
                      transition={{ repeat: Infinity, duration: 6 }}
                    />
                  </div>
                </div>

                {/* Skills cards */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { color: "from-primary/20 to-accent/10", delay: 0 },
                    { color: "from-accent/20 to-primary/10", delay: 0.2 },
                    { color: "from-primary/15 to-accent/15", delay: 0.4 },
                  ].map((card, n) => (
                    <motion.div 
                      key={n} 
                      className={`rounded-xl bg-gradient-to-br ${card.color} border border-primary/15 p-3 space-y-2`}
                      animate={{ y: [0, -3, 0] }}
                      transition={{ repeat: Infinity, duration: 3, delay: card.delay }}
                    >
                      <div className="w-7 h-7 rounded-lg bg-primary/25" />
                      <div className="h-1.5 w-full bg-primary/15 rounded" />
                      <div className="h-1.5 w-2/3 bg-primary/10 rounded" />
                    </motion.div>
                  ))}
                </div>

                {/* Chart mockup */}
                <div className="rounded-xl bg-card/80 border border-border/30 p-3 flex items-end gap-1.5 h-20">
                  {[35, 55, 75, 45, 85, 65, 80, 50, 90, 70].map((h, i) => (
                    <motion.div
                      key={i}
                      className="flex-1 rounded-t bg-gradient-to-t from-primary to-accent/60"
                      initial={{ height: 0 }}
                      animate={{ height: `${h}%` }}
                      transition={{ delay: 1.2 + i * 0.08, duration: 0.6, ease: "easeOut" }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Floating badges */}
            <motion.div 
              className="absolute -bottom-4 -left-6 px-4 py-2.5 rounded-xl bg-card border border-border/50 shadow-xl hidden md:flex items-center gap-2"
              animate={{ y: [0, -6, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
            >
              <div className="w-8 h-8 rounded-lg bg-enterprise-success/15 flex items-center justify-center">
                <Trophy className="w-4 h-4 text-enterprise-success" />
              </div>
              <div>
                <p className="text-xs font-bold">Ultra rapide</p>
                <p className="text-xs text-muted-foreground">Chargement instantané</p>
              </div>
            </motion.div>

            <motion.div 
              className="absolute -top-3 -right-4 px-4 py-2.5 rounded-xl bg-card border border-border/50 shadow-xl hidden md:flex items-center gap-2"
              animate={{ y: [0, -8, 0] }}
              transition={{ repeat: Infinity, duration: 4, delay: 1 }}
            >
              <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs font-bold">Visible sur Google</p>
                <p className="text-xs text-muted-foreground">Référencement optimisé</p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Trust bar */}
        <motion.div
          className="mt-12 sm:mt-16 pt-8 sm:pt-10 border-t border-border/30"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <p className="text-center text-[10px] sm:text-xs text-muted-foreground mb-4 sm:mb-5 uppercase tracking-[0.2em] font-semibold">
            Ce qui rend votre portfolio unique
          </p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {trustLogos.map((logo, i) => (
              <motion.span
                key={logo}
                className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-card/50 border border-border/30 text-xs sm:text-sm font-semibold text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all cursor-default"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.6 + i * 0.08 }}
                whileHover={{ scale: 1.05, y: -2 }}
              >
                {logo}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SellHero;
